import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Select } from '@/backoffice/components/ui';
import {
  loadClientSettings,
  saveClientSettings,
  type ClientSettings,
} from '@/domains/client/services/client.service';
import { Bell, Globe, Loader2, Save, Shield } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-gray-100 last:border-0">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors',
          checked ? 'bg-teal-600' : 'bg-gray-200',
        )}
      >
        <span
          className={cn(
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5',
            checked ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  );
}

export function SettingsPage() {
  const [settings, setSettings] = useState<ClientSettings>(loadClientSettings());
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setSettings(loadClientSettings());
  }, []);

  const handleSave = () => {
    setSaving(true);
    saveClientSettings(settings);
    setSuccess('Paramètres enregistrés.');
    setTimeout(() => setSaving(false), 400);
  };

  const update = <K extends keyof ClientSettings>(key: K, value: ClientSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSuccess(null);
  };

  return (
    <DashboardLayout
      userType="client"
      title="Paramètres"
      breadcrumbs={[
        { label: 'Tableau de bord', path: '/dashboard/client' },
        { label: 'Paramètres' },
      ]}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {success && (
          <div className="p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>
        )}

        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-teal-600" />
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            </div>

            <ToggleRow
              label="SMS"
              description="Recevoir les alertes de livraison et de paiement par SMS."
              checked={settings.notifySms}
              onChange={(v) => update('notifySms', v)}
            />
            <ToggleRow
              label="E-mail"
              description="Recevoir un récapitulatif de vos commandes par e-mail."
              checked={settings.notifyEmail}
              onChange={(v) => update('notifyEmail', v)}
            />
            <ToggleRow
              label="Notifications push"
              description="Alertes en temps réel dans l'application."
              checked={settings.notifyPush}
              onChange={(v) => update('notifyPush', v)}
            />
            <ToggleRow
              label="Actualités Onapila"
              description="Offres, nouveautés et conseils livraison."
              checked={settings.newsletter}
              onChange={(v) => update('newsletter', v)}
            />
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-teal-600" />
              <h3 className="text-lg font-semibold text-gray-900">Langue</h3>
            </div>
            <Select
              label="Langue de l'interface"
              value={settings.language}
              onChange={(e) => update('language', e.target.value as ClientSettings['language'])}
              options={[
                { value: 'fr', label: 'Français' },
                { value: 'en', label: 'English' },
              ]}
            />
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-teal-600" />
              <h3 className="text-lg font-semibold text-gray-900">Sécurité</h3>
            </div>
            <p className="text-sm text-gray-600">
              Pour modifier votre mot de passe, déconnectez-vous puis utilisez
              « Mot de passe oublié » sur la page de connexion.
            </p>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Enregistrer les paramètres
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
